/**
 * Helpers puros de Gobernanza (Sprint 286 I; REQ-MINT-013).
 *
 * - Filtros temporales 7/30/90/todos (+ rango) aplicados client-side.
 * - Resumen de uso real (rollups A): coste/tarea, cobertura y
 *   «muestra insuficiente» explícita; nunca estima.
 * - Deriva de proveedor: recuento por estado y pendientes fuera de SLA.
 * - Vistas guardadas por navegador (el usuario es el del Studio; no hay
 *   backend de preferencias en la entrega 2).
 */

import type { ObservabilityCell, PlatformCostFactsResponse, PriceProposalItem } from '../types';

export type PeriodKey = '7d' | '30d' | '90d' | 'all';

export interface PeriodRange {
  key: PeriodKey;
  from: string | null;
  to: string | null;
}

/** SLA de decisión de propuestas pendientes (aviso, no bloqueo). */
export const PROPOSAL_SLA_DAYS = 3;

/** Rango temporal del filtro (ISO); `all` no acota. */
export function periodRange(key: PeriodKey, now: Date): PeriodRange {
  if (key === 'all') return { key, from: null, to: null };
  const days = key === '7d' ? 7 : key === '30d' ? 30 : 90;
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return { key, from: from.toISOString(), to: now.toISOString() };
}

/** ¿La fecha cae dentro del rango (sin `from` ⇒ siempre dentro)? */
export function withinPeriod(iso: string | null | undefined, range: PeriodRange): boolean {
  if (!range.from) return true;
  if (!iso) return false;
  return iso >= range.from && (range.to === null || iso <= range.to);
}

export interface UsageSummary {
  tasks: number;
  cost: number;
  tokensIn: number;
  tokensOut: number;
  costPerTask: number | null;
  coverage: number | null;
  insufficient: boolean;
}

/** Resumen de uso real del periodo desde celdas de rollup (nunca estima). */
export function summarizeUsage(cells: ObservabilityCell[]): UsageSummary {
  const tasks = cells.reduce((acc, cell) => acc + cell.tasks_count, 0);
  const cost = cells.reduce((acc, cell) => acc + cell.cost, 0);
  const coverages = cells
    .map((cell) => cell.coverage)
    .filter((value): value is number => typeof value === 'number');
  return {
    tasks,
    cost,
    tokensIn: cells.reduce((acc, cell) => acc + cell.tokens_in, 0),
    tokensOut: cells.reduce((acc, cell) => acc + cell.tokens_out, 0),
    costPerTask: tasks > 0 ? cost / tasks : null,
    coverage: coverages.length > 0 ? Math.min(...coverages) : null,
    insufficient: tasks === 0,
  };
}

export interface UsageTrendPoint {
  period: string;
  cost: number;
  tasks: number;
}

/** Serie de tendencia (coste/tareas por periodo; ascendente por fecha). */
export function usageTrend(cells: ObservabilityCell[]): UsageTrendPoint[] {
  return [...cells]
    .sort((a, b) => (a.period_start < b.period_start ? -1 : 1))
    .map((cell) => ({ period: cell.period_start, cost: cell.cost, tasks: cell.tasks_count }));
}

/** Recuento de propuestas por estado (colas de gobernanza). */
export function summarizeProposals(items: PriceProposalItem[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const item of items) {
    counts[item.state] = (counts[item.state] ?? 0) + 1;
  }
  return counts;
}

/** Propuestas pendientes fuera de SLA (aviso operativo). */
export function pendingOverSla(
  items: PriceProposalItem[],
  now: Date,
  slaDays: number = PROPOSAL_SLA_DAYS,
): PriceProposalItem[] {
  const threshold = new Date(now.getTime() - slaDays * 24 * 60 * 60 * 1000).toISOString();
  return items.filter((item) => item.state === 'pending' && item.observed_at <= threshold);
}

export interface SavedView {
  name: string;
  period: PeriodKey;
  from: string;
  to: string;
  state: string;
  modelRef: string;
}

const STORAGE_KEY = 'model-intel-governance-views';

/** Carga vistas guardadas (JSON tolerante; sin storage ⇒ lista vacía). */
export function loadSavedViews(key: string = STORAGE_KEY): SavedView[] {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (view): view is SavedView =>
        !!view && typeof view === 'object' && typeof (view as SavedView).name === 'string',
    );
  } catch {
    return [];
  }
}

/** Persiste vistas guardadas (falla silenciosamente si el storage no está). */
export function persistSavedViews(views: SavedView[], key: string = STORAGE_KEY): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(views));
  } catch {
    // Storage no disponible: la vista simplemente no persiste.
  }
}

// ---------------------------------------------------------------------------
// Vista de plataforma (SystemAdmin; agregados M2M sin tenants — SEC-022)
// ---------------------------------------------------------------------------

export interface PlatformRegionSummary {
  region: string;
  ok: boolean;
  error?: string;
  versions: number;
  models: number;
  latest: string | null;
  sourceKinds: string[];
}

export interface PlatformSummary {
  regions: PlatformRegionSummary[];
  totalVersions: number;
  okRegions: number;
  failedRegions: number;
}

/** Agrega hechos de coste por región sin exponer identificadores de tenant. */
export function summarizePlatformFacts(data?: PlatformCostFactsResponse): PlatformSummary {
  const regions: PlatformRegionSummary[] = Object.entries(data?.regions ?? {})
    .map(([region, payload]) => {
      const items = payload.items ?? [];
      const models = new Set<string>();
      const sourceKinds = new Set<string>();
      let latest: string | null = null;
      for (const item of items) {
        models.add(item.model_ref ?? item.model);
        sourceKinds.add(item.source_kind);
        if (!latest || item.effective_from > latest) latest = item.effective_from;
      }
      return {
        region,
        ok: payload.ok,
        error: payload.error,
        versions: payload.total ?? items.length,
        models: models.size,
        latest,
        sourceKinds: [...sourceKinds].sort(),
      };
    })
    .sort((a, b) => a.region.localeCompare(b.region));
  return {
    regions,
    totalVersions: regions.reduce((acc, region) => acc + region.versions, 0),
    okRegions: regions.filter((region) => region.ok).length,
    failedRegions: regions.filter((region) => !region.ok).length,
  };
}
